import React, { FC, ReactNode, useCallback, useMemo, useState } from "react";

import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import { MenuInfo } from "rc-menu/lib/interface";
import { defineMessages, useIntl } from "react-intl";

import CountryRegion from "@mapmycustomers/shared/enum/CountryRegion";
import { SearchField } from "@mapmycustomers/ui";

import RoundButton from "@app/component/RoundButton";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getCountryRegionDisplayName } from "@app/util/ui";

import styles from "./RegionFilter.module.scss";

const messages = defineMessages({
  clear: {
    id: "map.layer.boundaries.territories.filterModal.filters.region.options.clear",
    defaultMessage: "Clear",
    description: "Clear option in the Region filter button",
  },
  label: {
    id: "map.layer.boundaries.territories.filterModal.filters.region.title",
    defaultMessage: "Region{hasValue, select, true {: {value}} other {}}",
    description: "Title of the Region filter button",
  },
  searchPlaceholder: {
    id: "map.layer.boundaries.territories.filterModal.filters.region.search.placeholder",
    defaultMessage: "Search",
    description: "Search placeholder in the Region filter button",
  },
});

const CLEAR_OPTION_VALUE = "clear";

const REGIONS = [
  CountryRegion.US_NORTHEAST,
  CountryRegion.US_MIDWEST,
  CountryRegion.US_SOUTH,
  CountryRegion.US_WEST,
  CountryRegion.CA_ATLANTIC,
  CountryRegion.CA_CENTRAL,
  CountryRegion.CA_NORTH,
  CountryRegion.CA_PRAIRIE,
  CountryRegion.CA_WEST_COAST,
];

export interface RegionFilterProps {
  onChange: (value: CountryRegion | undefined) => void;
  value?: CountryRegion;
}

const RegionFilter: FC<RegionFilterProps> = ({ onChange, value }) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const [search, setSearch] = useState("");

  const handleMenuClick = useCallback(
    (info: MenuInfo) => {
      analytics.using(["Type Filter"]);
      const value = info.key as CountryRegion | typeof CLEAR_OPTION_VALUE;
      if (value === CLEAR_OPTION_VALUE) {
        onChange(undefined);
      } else {
        onChange(value);
      }
    },
    [analytics, onChange]
  );

  const menuProps = useMemo(() => {
    const q = search.trim().toLowerCase();
    const items: MenuProps["items"] = [
      {
        className: styles.clearMenuItem,
        key: CLEAR_OPTION_VALUE,
        label: intl.formatMessage(messages.clear),
      },
      ...REGIONS.map((region) => ({
        key: region,
        label: getCountryRegionDisplayName(intl, region),
      })).filter(({ label }) => label.toLowerCase().includes(q)),
    ];
    return {
      activeKey: value,
      items,
      onClick: handleMenuClick,
    };
  }, [handleMenuClick, intl, value, search]);

  const getDropdown = useCallback(
    (menu: ReactNode) => {
      return (
        <div className={styles.customDropdown}>
          <SearchField
            className={styles.search}
            onChange={setSearch}
            placeholder={intl.formatMessage(messages.searchPlaceholder)}
            value={search}
          />
          {menu}
        </div>
      );
    },
    [intl, search]
  );

  return (
    <Dropdown
      dropdownRender={getDropdown}
      menu={menuProps}
      overlayClassName={styles.dropdownOverlay}
      trigger={["click"]}
    >
      <RoundButton active={!!value} hasMenu>
        {intl.formatMessage(messages.label, {
          hasValue: !!value,
          value: value ? getCountryRegionDisplayName(intl, value) : undefined,
        })}
      </RoundButton>
    </Dropdown>
  );
};

export default RegionFilter;
